/** @jsxImportSource @emotion/react */

import { CSSObject } from '@emotion/react';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { AppConfigContext, IMenuItemBadgeTextInfo } from './appConfigContext';
import { appContextReducer } from '../reducers/appContextReducer';
import request, {
  cancelAllRequests,
  clearSessionJwt,
  setClientLanguageHeader,
  setDeviceIdHeader,
  setLogoutInterceptorCallback,
  setSessionJwt,
  setSubdomainRouteMap,
} from '../utils/request';
import useNavigation from '../hooks/useNavigation';
import { removeSessionCookie, setSessionCookie } from '../utils/sessionCookie';
import { refreshSessionJwt } from '../utils/sessionJwt';
import {
  clearSessionPartner,
  saveSessionPartner,
} from '../utils/sessionPartner';
import { IRemoteImage } from '../components/RemoteImage';
import { IFlexNode } from '../views/flex/flexNode';
import useAdjustTracker from '../hooks/useAdjustTracker';
import useGtmEvents from '../hooks/useGtmEvents';
import useSnowplowTracker, {
  IStructuredSnowplowEvent,
} from '../hooks/useSnowplowTracker';
import useHttpCall, { IHttpCall } from '../hooks/useHttpCall';
import useOverlay from '../hooks/useOverlay';
import useLanguage from '../hooks/useLanguage';
import useIdleDetector from '../hooks/useIdleDetector';
import useWakeLock from '../hooks/useWakeLock';
import useTheme from '../hooks/useTheme';
import useWsPushNotification from '../hooks/useWsPushNotification';
import {
  enableRemoteLogging,
  logInfo,
  logWarning,
} from '../utils/remoteLogger';
import environment from '../utils/environment';
import { useServiceWorker } from '../hooks/useServiceWorker';
import currentPartner, { getLogoutRedirectUrl } from '../constants/partners';
import useRumTracker from '../hooks/useRumTracker';

let heartbeatTimeout: number | null;

export interface IAppContextState {
  userLoggedIn: boolean;
  idleDetectionEnabled: boolean;
  isCallActive: boolean;

  appMenuItems?: Array<{
    id: string;
    title: string;
    path: string;
    icon: IRemoteImage;
    selectedIcon: IRemoteImage;
    snowplowEvent?: IStructuredSnowplowEvent;

    flexNode?: IFlexNode;

    badge?: {
      textInfo?: IMenuItemBadgeTextInfo;
      onRouteVisitedEndpoint?: IHttpCall;
      resolvedText?: string;
    };
  }>;
}

interface IAppContext extends IAppContextState {
  updateMenuItemBadge: (id: string, textInfo?: IMenuItemBadgeTextInfo) => void;
  enableIdleDetection: (enable: boolean) => void;
  setCallActive: (active: boolean) => void;
  reinitializeApp: (urlToOpen?: string) => void;
  saveSession: (
    sessionId: string,
    deviceId: string,
    reinitialize?: boolean,
    urlToOpen?: string,
    jwt?: string
  ) => void;
  logout: (showConfirmation?: boolean, options?: { force?: boolean }) => void;
}

export const AppContext = createContext({} as IAppContext);

const initialState: IAppContextState = {
  userLoggedIn: false,
  idleDetectionEnabled: true,
  isCallActive: false,
};

interface IAppContextProvider {
  children: ReactNode;
}

export const AppContextProvider = ({ children }: IAppContextProvider) => {
  const [state, dispatch] = useReducer(appContextReducer, initialState);

  const {
    status,
    initialLaunch,
    isUserLoggedIn,
    appConfig,
    selectedLanguage,
    inferredLanguageKey,
    authorizedContent,
    startupMessage,
    refreshAppStartData,
  } = useContext(AppConfigContext);

  const countdown = appConfig.idleTimeCountdown;
  // Set state to trigger re-renders for countdown value.
  const [currentCountdown, setCurrentCountdown] = useState(countdown);

  const updateBadgeNotification = useWsPushNotification('update_tab_badge');
  const navigation = useNavigation();
  const overlay = useOverlay();
  const language = useLanguage();
  const { color, font } = useTheme();

  const { userIsIdle } = useIdleDetector({
    // Gate on idleDetectionEnabled, not just the global idleTimerEnabled, so the
    // detector pauses and resets userIsIdle whenever idle detection is turned off
    // — which is the entire duration of a call. This stops a userIsIdle value
    // latched before/during a call (e.g. a long tab-hidden wait in the waiting
    // room) from causing an instant logout if idle detection is briefly
    // re-enabled mid-call; re-enabling always starts a fresh idle window.
    enabled: appConfig.idleTimerEnabled && state.idleDetectionEnabled,
    timeToIdle: appConfig.idleTime,
  });

  useWakeLock(!state.idleDetectionEnabled);

  const adjustTracker = useAdjustTracker();
  const rumTracker = useRumTracker();
  const gtmTracker = useGtmEvents();
  const snowplowTracker = useSnowplowTracker();

  const handleHttpCall = useHttpCall();
  const { setSWContext } = useServiceWorker();

  // Update the dynamic menu item badge using the provided value
  // or fetch the deferred value using the item-specific endpoint.
  const updateMenuItemBadge = useCallback(
    (id: string, textInfo?: IMenuItemBadgeTextInfo) => {
      const dispatchUpdate = (itemId: string, text?: string) => {
        dispatch({
          type: 'SET_MENU_ITEM_BADGE_TEXT',
          payload: {
            id: itemId,
            text,
          },
        });
      };

      switch (textInfo?.type) {
        case 'deferred':
          handleHttpCall<IMenuItemBadgeTextInfo>(textInfo.endpoint, {
            persistent: true,
            silent: true,
          })
            .then((res) => {
              updateMenuItemBadge(id, res);
            })
            .catch(() => {
              // Fail silently.
            });
          break;

        case 'text':
          dispatchUpdate(id, textInfo.value);
          break;
      }
    },
    [handleHttpCall]
  );

  const enableIdleDetection = useCallback((enableDetection: boolean) => {
    dispatch({
      type: 'SET_IDLE_DETECTION_ENABLED',
      payload: enableDetection,
    });
  }, []);

  const setCallActive = useCallback((active: boolean) => {
    dispatch({
      type: 'SET_CALL_ACTIVE',
      payload: active,
    });
  }, []);

  const isUserLoggedInRef = useRef(isUserLoggedIn);
  isUserLoggedInRef.current = isUserLoggedIn;

  const isLoggingOutRef = useRef(false);

  const isIdleWarningActiveRef = useRef(false);

  const stopHeartbeat = () => {
    if (heartbeatTimeout !== null) {
      clearInterval(heartbeatTimeout);
      heartbeatTimeout = null;
    }
  };

  const reinitializeApp = useCallback(
    (urlToOpen?: string) => {
      refreshAppStartData(urlToOpen)
        .then(() => {
          navigation.redirectToPath(urlToOpen || '/', true);
        })
        .catch((error) => {
          logWarning('AppContext', 'Failed to reinitialize app', error);
        });
    },
    [navigation, refreshAppStartData]
  );

  const saveSession = useCallback(
    (
      sessionId: string,
      deviceId: string,
      reinitialize?: boolean,
      urlToOpen?: string,
      jwt?: string
    ) => {
      setSessionCookie(sessionId);
      setDeviceIdHeader(deviceId);
      saveSessionPartner();

      // Embedded sessions hand over the session's first JWT, because the
      // browser may not send the session cookie back from a partner iframe.
      // Everywhere else the cookie is enough, and a token left from a previous
      // session must not outlive it.
      if (jwt) {
        setSessionJwt(jwt);
      } else {
        clearSessionJwt();
      }
      setSWContext({
        sessionId,
        deviceId,
        appConfig,
        partnerId: currentPartner?.id,
        partnerBasePath: currentPartner?.basePath,
      });

      // Optionally reinit the app.
      if (reinitialize) {
        reinitializeApp(urlToOpen);
      }
    },
    [reinitializeApp, setSWContext, appConfig]
  );

  const logout = useCallback(
    (showConfirmation?: boolean, options?: { force?: boolean }) => {
      // `force` is for flows holding a server session that the client hasn't
      // reflected yet (appStart data not refetched), where isUserLoggedIn is
      // still false but there is a real session to tear down.
      if (
        (!isUserLoggedInRef.current && !options?.force) ||
        isLoggingOutRef.current
      ) {
        return;
      }

      const performLogout = () => {
        isLoggingOutRef.current = true;
        stopHeartbeat();
        cancelAllRequests();

        request('/api/view/logout')
          .catch((error) => {
            // Fail silently.
            logWarning('AppContext', 'Failed to log out the user', error);
          })
          .finally(() => {
            removeSessionCookie();
            clearSessionPartner();
            // Cleared only now, as the logout request itself needs the token.
            clearSessionJwt();
            gtmTracker.reset();
            setSWContext({
              sessionId: undefined,
              deviceId: undefined,
              appConfig: undefined,
              partnerId: currentPartner?.id,
              partnerBasePath: currentPartner?.basePath,
            });

            const logoutRedirectUrl = getLogoutRedirectUrl();
            if (logoutRedirectUrl) {
              // clearing session storage to have auto-login work correctly when redirected back to the app after logout
              sessionStorage.clear();
              window.location.replace(logoutRedirectUrl);
            } else {
              reinitializeApp();
            }
          });
      };

      if (showConfirmation) {
        overlay.presentAlert({
          title: language.get('logout_confirmation'),
          dismissible: true,
          items: [
            {
              title: language.get('cancel'),
              isCancel: true,
              onClick: () => {},
            },
            {
              title: language.get('logout'),
              isDestructive: true,
              onClick: performLogout,
            },
          ],
        });

        return;
      }

      performLogout();
    },
    [reinitializeApp, setSWContext, overlay, language, gtmTracker]
  );

  // Re-arm the logout guard when the user logs in.
  useEffect(() => {
    if (isUserLoggedIn) {
      isLoggingOutRef.current = false;
    }
  }, [isUserLoggedIn]);

  // Set the initial app state.
  useEffect(() => {
    dispatch({
      type: 'SET_APP_STATE',
      payload: {
        isUserLoggedIn,
        authorizedContent,
      },
    });
  }, [authorizedContent, isUserLoggedIn]);

  // Update client language request headers and the html document language attribute.
  useEffect(() => {
    const currentLanguage = selectedLanguage || inferredLanguageKey;

    setClientLanguageHeader(currentLanguage);
    document.documentElement.lang = currentLanguage;
  }, [inferredLanguageKey, selectedLanguage]);

  // Update the subdomain route map.
  useEffect(() => {
    setSubdomainRouteMap(appConfig.subdomainRouteMap);
  }, [appConfig]);

  // Set the logout interceptor callback, invoked when a request's session
  // verification concludes the session has expired. Skip it while a call is
  // active or reconnecting, so a false positive can't eject the patient
  // mid-call.
  useEffect(() => {
    setLogoutInterceptorCallback(() => {
      if (state.isCallActive) {
        logWarning(
          'AppContext',
          'Skipping forced logout from session verification during an active call'
        );
        return;
      }

      logout();
    });
  }, [logout, state.isCallActive]);

  // Set dynamic menu item badges.
  useEffect(() => {
    authorizedContent?.tabSpecification.tabs.forEach((tab) => {
      updateMenuItemBadge(tab.id, tab.badge?.textInfo);
    });
  }, [authorizedContent, updateMenuItemBadge]);

  // Start or stop the periodical heartbeat depending on the login state.
  useEffect(() => {
    if (isUserLoggedIn) {
      // Start heartbeat after stopping a potential previous one,
      // as the app might have been reinitialized.
      stopHeartbeat();

      // Refreshed on login and then with every heartbeat: a no-op unless the
      // session runs on a JWT. Logout stops the heartbeat first, so no refresh
      // races it. kry-user mints these with a 5 minute lifetime, which
      // pollInterval must stay well below.
      refreshSessionJwt();

      heartbeatTimeout = window.setInterval(() => {
        request('/api/user/heartbeat', {
          method: 'PUT',
          silent: true,
          persistent: true,
        }).catch(() => {});

        refreshSessionJwt();
      }, appConfig.pollInterval * 1000);
    } else {
      stopHeartbeat();
    }

    return () => {
      stopHeartbeat();
    };
  }, [appConfig.pollInterval, isUserLoggedIn]);

  // Set the Adjust tracker state, depending on appConfig.adjustState.
  // This can change during runtime as users can choose to opt-out of tracking.
  // Events collected while Adjust is in offline mode will be sent once/if switched back to online.
  useEffect(() => {
    switch (appConfig.adjustState) {
      case 'enabled':
        adjustTracker.enableTracking();
        adjustTracker.disableOfflineMode();
        // GTM: Enable tracking (sends queued events + enables immediate sending)
        gtmTracker.enableTracking();
        break;
      case 'disabled':
        adjustTracker.disableTracking();
        // GTM: Disable tracking (queues events but doesn't send)
        gtmTracker.disableTracking();
        break;
      case 'offline_mode':
        adjustTracker.enableOfflineMode();
        adjustTracker.enableTracking();
        // GTM: Disable tracking (queues events but doesn't send, same as disabled)
        gtmTracker.disableTracking();
        break;
    }
  }, [appConfig.adjustState, adjustTracker, gtmTracker]);

  // Set the Snowplow tracker state, depending on appConfig.snowplowEnabled.
  // This can change during runtime as users can choose to opt-out of tracking.
  useEffect(() => {
    if (appConfig.snowplowEnabled) {
      snowplowTracker.enableTracking();
    } else {
      snowplowTracker.disableTracking();
    }
  }, [appConfig.snowplowEnabled, snowplowTracker]);

  useEffect(() => {
    // Enable or disable RUM tracking based on datadogRumEnabled flag.
    if (appConfig.datadogRumEnabled) {
      rumTracker.enableTracking();
    } else {
      rumTracker.disableTracking();
    }
  }, [appConfig.datadogRumEnabled, rumTracker]);

  // Track the "app launch" Snowplow event.
  useEffect(() => {
    // The Snowplow event should be tracked on each relaunch.
    snowplowTracker.trackEvent(snowplowTracker.event.APP_OPEN);
  }, [status, initialLaunch, appConfig, snowplowTracker]);

  // Enable or disable remote logging.
  useEffect(() => {
    if (
      !environment.IS_DEV &&
      isUserLoggedIn &&
      appConfig.remoteLoggingEnabled
    ) {
      enableRemoteLogging(true);
    } else {
      enableRemoteLogging(false);
    }
  }, [appConfig, isUserLoggedIn]);

  // Handle the startupMessage, potentially provided in appStart.
  // The message should only be shown if the user is logged out.
  useEffect(() => {
    if (!isUserLoggedIn && startupMessage) {
      overlay.presentBasicAlert({
        title: startupMessage.title,
        message: startupMessage.message,
        persistent: true,
      });
    }
  }, [startupMessage, overlay, isUserLoggedIn]);

  // Reset the countdown at the start of each idle-warning cycle so one resuming
  // after a mid-call idle-detection flap starts fresh, not from a stale value.
  useEffect(() => {
    const isIdleWarningActive = Boolean(
      userIsIdle && state.idleDetectionEnabled && isUserLoggedIn
    );
    if (isIdleWarningActive && !isIdleWarningActiveRef.current) {
      setCurrentCountdown(countdown);
    }
    isIdleWarningActiveRef.current = isIdleWarningActive;
  }, [userIsIdle, state.idleDetectionEnabled, isUserLoggedIn, countdown]);

  // Handle automatic logout on user inactivity.
  useEffect(() => {
    // When the user becomes idle, show an alert with a countdown.
    // If the countdown reaches zero, logout.
    if (userIsIdle && state.idleDetectionEnabled && isUserLoggedIn) {
      const alertTitle = language.get('idle_alert_title');
      const alertButton = language.get('idle_alert_button');
      const alertMessageParts = language.get('idle_alert_message').split('XX');
      const counterStyle: CSSObject = {
        fontFamily: font.FAMILY_BOLD,
        color: color.ALERT,
      };
      const timeInterval = setInterval(() => {
        overlay.presentAlert({
          title: alertTitle,
          messageElement: (
            <span>
              <span>{alertMessageParts[0]}</span>
              <span css={counterStyle}>{currentCountdown} </span>
              <span>{alertMessageParts[1]}</span>
            </span>
          ),
          items: [
            {
              title: alertButton,
              onClick: () => {
                clearInterval(timeInterval);
                setCurrentCountdown(countdown);
              },
            },
          ],
        });
        setCurrentCountdown((prevCountdown) => {
          if (prevCountdown === 0) {
            clearInterval(timeInterval);
            overlay.dismiss();
            logout();
            return 0;
          }
          return prevCountdown - 1;
        });
      }, 1000);

      return () => {
        clearInterval(timeInterval);
      };
    }
  }, [
    appConfig.idleTimeCountdown,
    userIsIdle,
    font,
    color,
    language,
    overlay,
    state.idleDetectionEnabled,
    logout,
    currentCountdown,
    countdown,
    isUserLoggedIn,
  ]);

  useEffect(() => {
    logInfo(
      'AppContext',
      `idleDetection: ${state.idleDetectionEnabled ? 'enabled' : 'disabled'}`
    );
  }, [state.idleDetectionEnabled]);

  // Update menu badges on WebSocket push notification.
  useEffect(() => {
    if (updateBadgeNotification) {
      const { id, textInfo } = updateBadgeNotification.payload.tabBadgeUpdate;
      updateMenuItemBadge(id, textInfo);
    }
  }, [updateBadgeNotification, updateMenuItemBadge]);

  return (
    <AppContext.Provider
      value={{
        ...state,

        updateMenuItemBadge,
        enableIdleDetection,
        setCallActive,
        reinitializeApp,
        saveSession,
        logout,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
